/**
 * 集中化查询失效服务
 *
 * 统一管理所有领域操作对应的 React Query 失效规则，
 * 替代各 mutation/store action 中分散的手写 invalidateQueries 逻辑。
 *
 * 使用模块级 queryClient（非 hook），可在 Zustand store 等非组件代码中调用。
 */
import { queryClient } from '@/app/providers/query-client';
import {
  invalidateStoryboards,
  removeStoryboardDetailCache,
  invalidateStoryboardInfinite,
  invalidateStoryboardWindow,
} from '@/hooks/api/useStoryboards';
import { invalidateStoryboardBoardQueries } from '@/hooks/api/useStoryboardBoard';
import { invalidateScreenplayListRoot } from '@/hooks/api/useScreenplays';
import { removeStoryboardDialoguesCache } from '@/hooks/api/useStoryboardDialogues';
import { removeStoryboardVideosCache } from '@/hooks/api/useStoryboardVideos';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';

// ---------------------------------------------------------------------------
// 项目级操作
// ---------------------------------------------------------------------------

/**
 * 切换项目时：失效新目标项目相关的可变数据，确保拉取最新内容
 * 在 uiStore.selectProject 中调用
 *
 * 必须清除目标项目的模块级 bucket 缓存（storyboardsBucket/trackItemsBucket），
 * 否则 shotContentLayout 会使用上次访问时的旧 orderIndex 计算位置，导致看板错乱。
 */
export function invalidateOnProjectSwitch(projectId: string) {
  // 清除目标项目的分镜/轨道 bucket 缓存，防止使用旧排序数据计算位置
  clearStoryboardBoardCache(projectId);

  // 失效看板骨架与窗口查询（skeleton/window），确保拉取最新布局
  invalidateStoryboardBoardQueries(queryClient, projectId);

  invalidateStoryboards(queryClient, projectId);
  invalidateScreenplayListRoot(queryClient, projectId);
}

// ---------------------------------------------------------------------------
// 分镜级操作
// ---------------------------------------------------------------------------

/**
 * 删除分镜时：完整级联清理
 * - 分镜详情、关联子数据（items、对话、图片、视频）全部移除
 * - 分镜列表 + 看板全部失效
 */
export function invalidateOnStoryboardDelete(storyboardId: string, projectId: string) {
  clearStoryboardBoardCache(projectId);

  // 移除该分镜的所有子数据缓存
  removeStoryboardDetailCache(queryClient, storyboardId);
  queryClient.removeQueries({ queryKey: ['storyboard-items', storyboardId] });
  removeStoryboardDialoguesCache(queryClient, storyboardId);
  queryClient.removeQueries({ queryKey: ['storyboard-images', storyboardId] });
  removeStoryboardVideosCache(queryClient, storyboardId);

  // 失效列表与看板
  invalidateStoryboards(queryClient, projectId);
  invalidateStoryboardBoardQueries(queryClient, projectId);

  invalidateStoryboardInfinite(queryClient, projectId);
  invalidateStoryboardWindow(queryClient, projectId);
}

/**
 * 更新分镜时：失效列表与看板（供需要统一失效的场景复用）
 */
export function invalidateOnStoryboardUpdate(projectId: string) {
  invalidateStoryboards(queryClient, projectId);
  invalidateStoryboardBoardQueries(queryClient, projectId);
}
